import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Complete translation dictionaries
const translations = {
  en: {
    // Navigation
    'nav.features': 'Features',
    'nav.pricing': 'Pricing',
    'nav.faq': 'FAQ',
    'nav.feedback': 'Feedback',
    'nav.contact': 'Contact Us',
    'nav.login': 'Log in',
    'nav.getStarted': 'Start Free Trial',
    'nav.dashboard': 'Dashboard',
    'nav.back': 'Back',
    'nav.backToHome': 'Back to home',
    
    // Hero - Etsy-focused
    'hero.title1': 'Create Etsy Listing Photos',
    'hero.title2': 'That Sell',
    'hero.subtitle': 'Turn your designs into high-converting product images in seconds.',
    'hero.cta': 'Generate My First Image',
    'hero.seeFeatures': 'See How It Works',
    'hero.trial': 'No credit card required',
    
    // Features
    'features.title': 'Built for Etsy Sellers',
    'features.subtitle': 'Create professional listing photos that convert browsers into buyers',
    'features.imageToPrompt': 'Image to Prompt',
    'features.imageToPromptDesc': 'Upload any image and get detailed prompts for AI recreation. The best image to prompt generator for product creators.',
    'features.promptEnhancer': 'Prompt Enhancer',
    'features.promptEnhancerDesc': 'Transform basic prompts into high-quality AI instructions that generate better results.',
    'features.imageGenerator': 'AI Image Generator',
    'features.imageGeneratorDesc': 'Generate stunning product images, stickers, and illustrations with AI.',
    'features.mockupGenerator': 'Etsy Listing Generator',
    'features.mockupGeneratorDesc': 'Create high-converting product photos for Etsy, Shopify, and e-commerce stores.',
    'features.backgroundGenerator': 'Background Generator',
    'features.backgroundGeneratorDesc': 'Generate or replace backgrounds to match your product aesthetic.',
    'features.workflow': 'Workflow System',
    'features.workflowDesc': 'Connect all AI mockup tools in one seamless creative flow.',
    
    // Showcase
    'showcase.title': 'See What You Can Create',
    'showcase.subtitle': 'Professional listing photos in 3 simple steps',
    'showcase.aiPowered': 'AI-Powered Results',
    'showcase.upload': 'Upload Design',
    'showcase.aiMagic': 'Select Style',
    'showcase.result': 'Download',
    'showcase.bottomText': 'Stop paying for expensive mockups. Create unlimited listing photos.',
    
    // Pricing
    'pricing.title': 'Simple, Transparent Pricing',
    'pricing.subtitle': 'Start free. Upgrade when you\'re ready.',
    'pricing.mostPopular': 'Most Popular',
    'pricing.perMonth': '/month',
    'pricing.credits': 'credits/month',
    'pricing.getStarted': 'Get Started Free',
    
    // CTA
    'cta.title': 'Ready to boost your Etsy sales?',
    'cta.subtitle': 'Join thousands of sellers creating high-converting listing photos',
    
    // Footer
    'footer.rights': '© 2026 MockupPro. All rights reserved.',
    
    // Auth
    'auth.createAccount': 'Create your account',
    'auth.welcomeBack': 'Welcome back',
    'auth.trialInfo': 'Create unlimited mockups for your Etsy listings',
    'auth.signInInfo': 'Sign in to access your dashboard',
    'auth.firstName': 'First Name',
    'auth.firstNamePlaceholder': 'John',
    'auth.lastName': 'Last Name',
    'auth.lastNamePlaceholder': 'Doe',
    'auth.name': 'Name',
    'auth.namePlaceholder': 'Your name',
    'auth.email': 'Email',
    'auth.emailPlaceholder': 'you@example.com',
    'auth.password': 'Password',
    'auth.startTrial': 'Get Started Free',
    'auth.signIn': 'Sign In',
    'auth.processing': 'Processing...',
    'auth.haveAccount': 'Already have an account?',
    'auth.noAccount': 'Don\'t have an account?',
    'auth.signInLink': 'Sign in',
    'auth.startTrialLink': 'Get started free',
    'auth.rightTitle': 'One platform, endless possibilities',
    'auth.rightSubtitle': 'Generate prompts, create images, design mockups—all in one seamless workflow built for creators.',
    
    // Forgot Password
    'auth.forgotPasswordLink': 'Forgot password?',
    'auth.forgotPassword': 'Forgot your password?',
    'auth.forgotPasswordInfo': 'Enter your email address and we\'ll send you a link to reset your password.',
    'auth.sendResetLink': 'Send Reset Link',
    'auth.backToLogin': 'Back to login',
    'auth.rememberPassword': 'Remember your password?',
    'auth.checkYourEmail': 'Check your email',
    'auth.resetLinkSent': 'Password reset link sent!',
    'auth.resetLinkSentTo': 'We\'ve sent a password reset link to',
    'auth.didntReceiveEmail': 'Didn\'t receive the email? Check your spam folder or try again.',
    'auth.tryAgain': 'Try Again',
    'auth.resetRightTitle': 'Password Recovery',
    'auth.resetRightSubtitle': 'We\'ll help you get back into your account securely.',
    'auth.resetError': 'Something went wrong. Please try again.',
    
    // Reset Password
    'auth.resetPassword': 'Reset your password',
    'auth.resetPasswordInfo': 'Enter your new password below.',
    'auth.resettingFor': 'Resetting password for',
    'auth.newPassword': 'New Password',
    'auth.confirmPassword': 'Confirm Password',
    'auth.passwordRequirements': 'Must be at least 6 characters',
    'auth.passwordsDoNotMatch': 'Passwords do not match',
    'auth.passwordTooShort': 'Password must be at least 6 characters',
    'auth.resetPasswordBtn': 'Reset Password',
    'auth.passwordResetSuccess': 'Password reset successfully!',
    'auth.passwordResetComplete': 'Password Reset Complete',
    'auth.passwordResetCompleteInfo': 'Your password has been reset successfully. You can now log in with your new password.',
    'auth.redirectingToLogin': 'Redirecting to login...',
    'auth.signInNow': 'Sign In Now',
    'auth.verifyingToken': 'Verifying reset link...',
    'auth.tokenMissing': 'Missing Reset Link',
    'auth.tokenMissingInfo': 'No reset token found. Please request a new password reset link.',
    'auth.tokenExpired': 'Link Expired',
    'auth.tokenExpiredInfo': 'This password reset link has expired. Please request a new one.',
    'auth.tokenInvalid': 'Invalid Link',
    'auth.tokenInvalidInfo': 'This password reset link is invalid. Please request a new one.',
    'auth.requestNewLink': 'Request New Link',
    'auth.secureReset': 'Secure Password Reset',
    'auth.secureResetInfo': 'Your password reset link is secure and expires in 1 hour.',
    
    // Email Verification
    'verify.bannerTitle': 'Please verify your email',
    'verify.bannerMessage': 'Check your inbox and click the verification link to unlock all features.',
    'verify.resendEmail': 'Resend Email',
    'verify.sending': 'Sending...',
    'verify.emailSent': 'Verification email sent!',
    'verify.clickToVerify': 'Click to verify',
    'verify.verifying': 'Verifying your email...',
    'verify.success': 'Email verified successfully!',
    'verify.successTitle': 'Email Verified!',
    'verify.successMessage': 'Your email has been verified. You now have full access to all features.',
    'verify.redirecting': 'Redirecting to dashboard...',
    'verify.goToDashboard': 'Go to Dashboard',
    'verify.expiredTitle': 'Link Expired',
    'verify.expiredMessage': 'This verification link has expired. Please request a new one.',
    'verify.resendLink': 'Resend Verification Email',
    'verify.resending': 'Sending...',
    'verify.invalidTitle': 'Invalid Link',
    'verify.invalidMessage': 'This verification link is invalid or has already been used.',
    'verify.backToLogin': 'Back to Login',
    'verify.tokenMissing': 'No verification token found.',
    'verify.error': 'Something went wrong. Please try again.',
    'verify.resendError': 'Failed to send verification email. Please try again.',
    'verify.alreadyVerified': 'Your email is already verified!',
    'verify.noEmail': 'No email address found.',
    'verify.resent': 'Verification email sent!',
    'verify.resentTitle': 'Email Sent!',
    'verify.resentMessage': 'A new verification link has been sent to your email.',
    'verify.rightTitle': 'Email Verification',
    'verify.rightSubtitle': 'Verify your email to unlock all MockupPro features and keep your account secure.',
    
    // Dashboard
    'dashboard.welcome': 'Welcome back',
    'dashboard.whatCreate': 'What would you like to create today?',
    'dashboard.credits': 'Credits Remaining',
    'dashboard.plan': 'Current Plan',
    'dashboard.creations': 'Total Creations',
    'dashboard.quickActions': 'Quick Actions',
    'dashboard.recentCreations': 'Recent Creations',
    'dashboard.noCreations': 'No creations yet',
    'dashboard.startCreating': 'Start Creating',
    'dashboard.tools': 'AI Tools',
    'dashboard.gallery': 'Gallery',
    'dashboard.settings': 'Settings',
    'dashboard.logout': 'Logout',
    
    // Tools
    'tools.imageToPrompt': 'Image to Prompt',
    'tools.imageToPromptDesc': 'Extract detailed prompts from any image',
    'tools.promptEnhancer': 'Prompt Enhancer',
    'tools.promptEnhancerDesc': 'Improve and optimize your prompts',
    'tools.imageGenerator': 'Image Generator',
    'tools.imageGeneratorDesc': 'Create AI-generated images',
    'tools.mockupGenerator': 'Mockup Generator',
    'tools.mockupGeneratorDesc': 'Create product mockups',
    'tools.backgroundGenerator': 'Background Generator',
    'tools.backgroundGeneratorDesc': 'Generate custom backgrounds',
    'tools.workflow': 'Full Workflow',
    'tools.workflowDesc': 'Complete creative pipeline',
    
    // FAQ Page
    'faq.title': 'Frequently Asked Questions',
    'faq.subtitle': 'Quick answers to common questions',
    'faq.stillHaveQuestions': 'Still have questions?',
    'faq.hereToHelp': 'We\'re here to help',
    'faq.contactSupport': 'Contact Support',
    'faq.gettingStarted': 'Getting Started',
    'faq.billing': 'Billing & Subscription',
    'faq.creditsUsage': 'Credits & Usage',
    'faq.features': 'Features',
    'faq.accountCancellation': 'Account & Cancellation',
    
    // Contact Page
    'contact.title': 'Send Feedback',
    'contact.subtitle': 'Found an issue or have an idea? We\'d love to hear from you. Use the form below to share feedback, report a problem, or ask a question.',
    'contact.name': 'Name',
    'contact.namePlaceholder': 'Your name',
    'contact.email': 'Email',
    'contact.emailPlaceholder': 'you@example.com',
    'contact.topic': 'Topic',
    'contact.topicPlaceholder': 'Select a topic',
    'contact.message': 'Message',
    'contact.messagePlaceholder': 'Tell us what\'s on your mind...',
    'contact.send': 'Send Feedback',
    'contact.sending': 'Sending...',
    'contact.directEmail': 'Or email us directly at',
    'contact.successTitle': 'Feedback Received',
    'contact.successMessage': 'Thanks for sharing! We\'ll review your feedback and get back to you if needed.',
    'contact.backToHome': 'Back to Home',
    'contact.sendAnother': 'Send More Feedback',
    'contact.topics.bug': 'Report a Bug',
    'contact.topics.feature': 'Request a Feature',
    'contact.topics.general': 'General Feedback',
    'contact.topics.billing': 'Billing Question',
    'contact.topics.other': 'Other',
    'contact.followUp': 'Can we follow up with you?',
    
    // Common
    'common.upload': 'Upload',
    'common.generate': 'Generate',
    'common.download': 'Download',
    'common.share': 'Share',
    'common.delete': 'Delete',
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.loading': 'Loading...',
    'common.credits': 'credits',
    'common.error': 'Error',
    'common.success': 'Success',
    
    // Billing
    'billing.plan': 'Plan',
    'billing.amount': 'Amount',
    'billing.redirecting': 'Redirecting to checkout...',
    'billing.proceedToCheckout': 'Proceed to Checkout',
    'billing.secureCheckout': 'Secure checkout powered by Stripe',
    'billing.manageBilling': 'Manage Billing',
    'billing.cancelAnytime': 'Cancel anytime',
  },
  es: {
    // Navigation
    'nav.features': 'Características',
    'nav.pricing': 'Precios',
    'nav.faq': 'Preguntas',
    'nav.feedback': 'Comentarios',
    'nav.contact': 'Contacto',
    'nav.login': 'Iniciar sesión',
    'nav.getStarted': 'Prueba Gratis',
    'nav.dashboard': 'Panel',
    'nav.back': 'Volver',
    'nav.backToHome': 'Volver al inicio',
    
    // Hero
    'hero.title1': 'Crea Mockups, Prompts y Diseños en',
    'hero.title2': 'Un Solo Flujo',
    'hero.subtitle': 'Sube cualquier imagen. Genera prompts que convierten. Crea mockups y fondos realistas sin cambiar entre herramientas.',
    'hero.cta': 'Prueba Gratis',
    'hero.seeFeatures': 'Ver Características',
    'hero.trial': '7 días gratis • 120 créditos • Cancela cuando quieras',
    
    // Features
    'features.title': 'Todo lo que necesitas',
    'features.subtitle': 'Seis herramientas IA en una plataforma',
    'features.imageToPrompt': 'Imagen a Prompt',
    'features.imageToPromptDesc': 'Sube cualquier imagen y obtén prompts detallados',
    'features.promptEnhancer': 'Mejorador de Prompts',
    'features.promptEnhancerDesc': 'Transforma prompts básicos en instrucciones de alta calidad',
    'features.imageGenerator': 'Generador de Imágenes IA',
    'features.imageGeneratorDesc': 'Genera impresionantes imágenes de productos y stickers',
    'features.mockupGenerator': 'Generador de Mockups',
    'features.mockupGeneratorDesc': 'Crea mockups realistas con fondos lifestyle',
    'features.backgroundGenerator': 'Generador de Fondos',
    'features.backgroundGeneratorDesc': 'Genera o reemplaza fondos para tu producto',
    'features.workflow': 'Sistema de Flujo',
    'features.workflowDesc': 'Conecta todas las herramientas en un flujo creativo',
    
    // Showcase
    'showcase.title': 'Mira lo que puedes crear',
    'showcase.subtitle': 'Genera prompts, diseños y mockups realistas en segundos.',
    'showcase.aiPowered': 'Resultados con IA',
    'showcase.upload': 'Subir',
    'showcase.aiMagic': 'Magia IA',
    'showcase.result': 'Resultado',
    'showcase.bottomText': 'Esto reemplaza múltiples herramientas. Una plataforma, posibilidades infinitas.',
    
    // Pricing
    'pricing.title': 'Precios simples y transparentes',
    'pricing.subtitle': 'Comienza con una prueba gratis. Mejora cuando estés listo.',
    'pricing.mostPopular': 'Más Popular',
    'pricing.perMonth': '/mes',
    'pricing.credits': 'créditos/mes',
    'pricing.getStarted': 'Comenzar',
    
    // CTA
    'cta.title': '¿Listo para optimizar tu flujo de trabajo?',
    'cta.subtitle': 'Únete a miles de creadores ahorrando horas cada semana',
    
    // Footer
    'footer.rights': '© 2026 MockupPro. Todos los derechos reservados.',
    
    // Auth
    'auth.createAccount': 'Crea tu cuenta',
    'auth.welcomeBack': 'Bienvenido de nuevo',
    'auth.trialInfo': 'Crea mockups ilimitados para tus listados de Etsy',
    'auth.signInInfo': 'Inicia sesión para acceder a tu panel',
    'auth.firstName': 'Nombre',
    'auth.firstNamePlaceholder': 'Juan',
    'auth.lastName': 'Apellido',
    'auth.lastNamePlaceholder': 'García',
    'auth.name': 'Nombre',
    'auth.namePlaceholder': 'Tu nombre',
    'auth.email': 'Correo electrónico',
    'auth.emailPlaceholder': 'tu@ejemplo.com',
    'auth.password': 'Contraseña',
    'auth.startTrial': 'Comenzar Prueba Gratis',
    'auth.signIn': 'Iniciar Sesión',
    'auth.processing': 'Procesando...',
    'auth.haveAccount': '¿Ya tienes una cuenta?',
    'auth.noAccount': '¿No tienes una cuenta?',
    'auth.signInLink': 'Iniciar sesión',
    'auth.startTrialLink': 'Comenzar prueba gratis',
    'auth.rightTitle': 'Una plataforma, posibilidades infinitas',
    'auth.rightSubtitle': 'Genera prompts, crea imágenes, diseña mockups—todo en un flujo de trabajo perfecto.',
    
    // Forgot Password
    'auth.forgotPasswordLink': '¿Olvidaste tu contraseña?',
    'auth.forgotPassword': '¿Olvidaste tu contraseña?',
    'auth.forgotPasswordInfo': 'Ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.',
    'auth.sendResetLink': 'Enviar Enlace',
    'auth.backToLogin': 'Volver al inicio de sesión',
    'auth.rememberPassword': '¿Recuerdas tu contraseña?',
    'auth.checkYourEmail': 'Revisa tu correo',
    'auth.resetLinkSent': '¡Enlace de restablecimiento enviado!',
    'auth.resetLinkSentTo': 'Hemos enviado un enlace de restablecimiento a',
    'auth.didntReceiveEmail': '¿No recibiste el correo? Revisa tu carpeta de spam o intenta de nuevo.',
    'auth.tryAgain': 'Intentar de Nuevo',
    'auth.resetRightTitle': 'Recuperación de Contraseña',
    'auth.resetRightSubtitle': 'Te ayudaremos a recuperar tu cuenta de forma segura.',
    'auth.resetError': 'Algo salió mal. Por favor intenta de nuevo.',
    'auth.resetPassword': 'Restablece tu contraseña',
    'auth.resetPasswordInfo': 'Ingresa tu nueva contraseña a continuación.',
    'auth.resettingFor': 'Restableciendo contraseña para',
    'auth.newPassword': 'Nueva Contraseña',
    'auth.confirmPassword': 'Confirmar Contraseña',
    'auth.passwordRequirements': 'Debe tener al menos 6 caracteres',
    'auth.passwordsDoNotMatch': 'Las contraseñas no coinciden',
    'auth.passwordTooShort': 'La contraseña debe tener al menos 6 caracteres',
    'auth.resetPasswordBtn': 'Restablecer Contraseña',
    'auth.passwordResetSuccess': '¡Contraseña restablecida exitosamente!',
    'auth.passwordResetComplete': 'Contraseña Restablecida',
    'auth.passwordResetCompleteInfo': 'Tu contraseña ha sido restablecida. Ya puedes iniciar sesión con tu nueva contraseña.',
    'auth.redirectingToLogin': 'Redirigiendo al inicio de sesión...',
    'auth.signInNow': 'Iniciar Sesión Ahora',
    'auth.verifyingToken': 'Verificando enlace...',
    'auth.tokenMissing': 'Enlace Faltante',
    'auth.tokenMissingInfo': 'No se encontró token. Por favor solicita un nuevo enlace.',
    'auth.tokenExpired': 'Enlace Expirado',
    'auth.tokenExpiredInfo': 'Este enlace ha expirado. Por favor solicita uno nuevo.',
    'auth.tokenInvalid': 'Enlace Inválido',
    'auth.tokenInvalidInfo': 'Este enlace es inválido. Por favor solicita uno nuevo.',
    'auth.requestNewLink': 'Solicitar Nuevo Enlace',
    'auth.secureReset': 'Restablecimiento Seguro',
    'auth.secureResetInfo': 'Tu enlace es seguro y expira en 1 hora.',
    
    // Email Verification
    'verify.bannerTitle': 'Por favor verifica tu correo',
    'verify.bannerMessage': 'Revisa tu bandeja de entrada y haz clic en el enlace de verificación.',
    'verify.resendEmail': 'Reenviar Correo',
    'verify.sending': 'Enviando...',
    'verify.emailSent': '¡Correo de verificación enviado!',
    'verify.clickToVerify': 'Clic para verificar',
    'verify.verifying': 'Verificando tu correo...',
    'verify.success': '¡Correo verificado exitosamente!',
    'verify.successTitle': '¡Correo Verificado!',
    'verify.successMessage': 'Tu correo ha sido verificado. Ahora tienes acceso completo.',
    'verify.redirecting': 'Redirigiendo al panel...',
    'verify.goToDashboard': 'Ir al Panel',
    'verify.expiredTitle': 'Enlace Expirado',
    'verify.expiredMessage': 'Este enlace ha expirado. Por favor solicita uno nuevo.',
    'verify.resendLink': 'Reenviar Enlace',
    'verify.resending': 'Enviando...',
    'verify.invalidTitle': 'Enlace Inválido',
    'verify.invalidMessage': 'Este enlace es inválido o ya fue usado.',
    'verify.backToLogin': 'Volver al Login',
    'verify.tokenMissing': 'No se encontró token de verificación.',
    'verify.error': 'Algo salió mal. Intenta de nuevo.',
    'verify.resendError': 'Error al enviar. Intenta de nuevo.',
    'verify.alreadyVerified': '¡Tu correo ya está verificado!',
    'verify.noEmail': 'No se encontró correo.',
    'verify.resent': '¡Correo de verificación enviado!',
    'verify.resentTitle': '¡Correo Enviado!',
    'verify.resentMessage': 'Se envió un nuevo enlace a tu correo.',
    'verify.rightTitle': 'Verificación de Correo',
    'verify.rightSubtitle': 'Verifica tu correo para acceder a todas las funciones.',
    
    // Dashboard
    'dashboard.welcome': 'Bienvenido de nuevo',
    'dashboard.whatCreate': '¿Qué te gustaría crear hoy?',
    'dashboard.credits': 'Créditos Restantes',
    'dashboard.plan': 'Plan Actual',
    'dashboard.creations': 'Creaciones Totales',
    'dashboard.quickActions': 'Acciones Rápidas',
    'dashboard.recentCreations': 'Creaciones Recientes',
    'dashboard.noCreations': 'Aún no hay creaciones',
    'dashboard.startCreating': 'Comenzar a Crear',
    'dashboard.tools': 'Herramientas IA',
    'dashboard.gallery': 'Galería',
    'dashboard.settings': 'Configuración',
    'dashboard.logout': 'Cerrar sesión',
    
    // Tools
    'tools.imageToPrompt': 'Imagen a Prompt',
    'tools.imageToPromptDesc': 'Extrae prompts detallados de cualquier imagen',
    'tools.promptEnhancer': 'Mejorador de Prompts',
    'tools.promptEnhancerDesc': 'Mejora y optimiza tus prompts',
    'tools.imageGenerator': 'Generador de Imágenes',
    'tools.imageGeneratorDesc': 'Crea imágenes con IA',
    'tools.mockupGenerator': 'Generador de Mockups',
    'tools.mockupGeneratorDesc': 'Crea mockups de productos',
    'tools.backgroundGenerator': 'Generador de Fondos',
    'tools.backgroundGeneratorDesc': 'Genera fondos personalizados',
    'tools.workflow': 'Flujo Completo',
    'tools.workflowDesc': 'Pipeline creativo completo',
    
    // FAQ Page
    'faq.title': 'Preguntas Frecuentes',
    'faq.subtitle': 'Respuestas rápidas a preguntas comunes',
    'faq.stillHaveQuestions': '¿Aún tienes preguntas?',
    'faq.hereToHelp': 'Estamos aquí para ayudar',
    'faq.contactSupport': 'Contactar Soporte',
    'faq.gettingStarted': 'Comenzando',
    'faq.billing': 'Facturación y Suscripción',
    'faq.creditsUsage': 'Créditos y Uso',
    'faq.features': 'Características',
    'faq.accountCancellation': 'Cuenta y Cancelación',
    
    // Contact Page
    'contact.title': 'Enviar Comentarios',
    'contact.subtitle': '¿Encontraste un problema o tienes una idea? Nos encantaría saber de ti. Usa el formulario para compartir comentarios, reportar un problema o hacer una pregunta.',
    'contact.name': 'Nombre',
    'contact.namePlaceholder': 'Tu nombre',
    'contact.email': 'Correo electrónico',
    'contact.emailPlaceholder': 'tu@ejemplo.com',
    'contact.topic': 'Tema',
    'contact.topicPlaceholder': 'Selecciona un tema',
    'contact.message': 'Mensaje',
    'contact.messagePlaceholder': 'Cuéntanos qué piensas...',
    'contact.send': 'Enviar Comentarios',
    'contact.sending': 'Enviando...',
    'contact.directEmail': 'O escríbenos directamente a',
    'contact.successTitle': 'Comentarios Recibidos',
    'contact.successMessage': '¡Gracias por compartir! Revisaremos tus comentarios y te contactaremos si es necesario.',
    'contact.backToHome': 'Volver al Inicio',
    'contact.sendAnother': 'Enviar Más Comentarios',
    'contact.topics.bug': 'Reportar un Error',
    'contact.topics.feature': 'Solicitar una Función',
    'contact.topics.general': 'Comentarios Generales',
    'contact.topics.billing': 'Pregunta de Facturación',
    'contact.topics.other': 'Otro',
    'contact.followUp': '¿Podemos contactarte?',
    
    // Common
    'common.upload': 'Subir',
    'common.generate': 'Generar',
    'common.download': 'Descargar',
    'common.share': 'Compartir',
    'common.delete': 'Eliminar',
    'common.cancel': 'Cancelar',
    'common.save': 'Guardar',
    'common.loading': 'Cargando...',
    'common.credits': 'créditos',
    'common.error': 'Error',
    'common.success': 'Éxito',
  },
  fr: {
    // Navigation
    'nav.features': 'Fonctionnalités',
    'nav.pricing': 'Tarifs',
    'nav.faq': 'FAQ',
    'nav.feedback': 'Commentaires',
    'nav.contact': 'Contact',
    'nav.login': 'Connexion',
    'nav.getStarted': 'Essai Gratuit',
    'nav.dashboard': 'Tableau de bord',
    'nav.back': 'Retour',
    'nav.backToHome': 'Retour à l\'accueil',
    
    // Hero
    'hero.title1': 'Créez Mockups, Prompts et Designs en',
    'hero.title2': 'Un Seul Workflow',
    'hero.subtitle': 'Téléchargez une image. Générez des prompts performants. Créez des mockups et arrière-plans réalistes sans changer d\'outils.',
    'hero.cta': 'Essai Gratuit',
    'hero.seeFeatures': 'Voir les Fonctionnalités',
    'hero.trial': '7 jours gratuits • 120 crédits • Annulez à tout moment',
    
    // Features
    'features.title': 'Tout ce dont vous avez besoin',
    'features.subtitle': 'Six outils IA dans une plateforme unifiée',
    'features.imageToPrompt': 'Image vers Prompt',
    'features.imageToPromptDesc': 'Téléchargez n\'importe quelle image et obtenez des prompts détaillés',
    'features.promptEnhancer': 'Améliorateur de Prompts',
    'features.promptEnhancerDesc': 'Transformez des prompts basiques en instructions de haute qualité',
    'features.imageGenerator': 'Générateur d\'Images IA',
    'features.imageGeneratorDesc': 'Générez de superbes images de produits et stickers',
    'features.mockupGenerator': 'Générateur de Mockups',
    'features.mockupGeneratorDesc': 'Créez des mockups réalistes avec des arrière-plans lifestyle',
    'features.backgroundGenerator': 'Générateur d\'Arrière-plans',
    'features.backgroundGeneratorDesc': 'Générez ou remplacez les arrière-plans de vos produits',
    'features.workflow': 'Système de Flux',
    'features.workflowDesc': 'Connectez tous les outils dans un flux créatif fluide',
    
    // Showcase
    'showcase.title': 'Voyez ce que vous pouvez créer',
    'showcase.subtitle': 'Générez des prompts, designs et mockups en quelques secondes.',
    'showcase.aiPowered': 'Résultats IA',
    'showcase.upload': 'Télécharger',
    'showcase.aiMagic': 'Magie IA',
    'showcase.result': 'Résultat',
    'showcase.bottomText': 'Cela remplace plusieurs outils. Une plateforme, des possibilités infinies.',
    
    // Pricing
    'pricing.title': 'Tarifs simples et transparents',
    'pricing.subtitle': 'Commencez avec un essai gratuit. Passez au niveau supérieur quand vous êtes prêt.',
    'pricing.mostPopular': 'Plus Populaire',
    'pricing.perMonth': '/mois',
    'pricing.credits': 'crédits/mois',
    'pricing.getStarted': 'Commencer',
    
    // CTA
    'cta.title': 'Prêt à optimiser votre flux de travail?',
    'cta.subtitle': 'Rejoignez des milliers de créateurs qui économisent des heures chaque semaine',
    
    // Footer
    'footer.rights': '© 2026 MockupPro. Tous droits réservés.',
    
    // Auth
    'auth.createAccount': 'Créez votre compte',
    'auth.welcomeBack': 'Bienvenue',
    'auth.trialInfo': 'Créez des mockups illimités pour vos annonces Etsy',
    'auth.signInInfo': 'Connectez-vous pour accéder à votre tableau de bord',
    'auth.firstName': 'Prénom',
    'auth.firstNamePlaceholder': 'Jean',
    'auth.lastName': 'Nom',
    'auth.lastNamePlaceholder': 'Dupont',
    'auth.name': 'Nom',
    'auth.namePlaceholder': 'Votre nom',
    'auth.email': 'E-mail',
    'auth.emailPlaceholder': 'vous@exemple.com',
    'auth.password': 'Mot de passe',
    'auth.startTrial': 'Commencer l\'Essai Gratuit',
    'auth.signIn': 'Se Connecter',
    'auth.processing': 'Traitement...',
    'auth.haveAccount': 'Vous avez déjà un compte?',
    'auth.noAccount': 'Pas encore de compte?',
    'auth.signInLink': 'Se connecter',
    'auth.startTrialLink': 'Essai gratuit',
    'auth.rightTitle': 'Une plateforme, des possibilités infinies',
    'auth.rightSubtitle': 'Générez des prompts, créez des images, concevez des mockups—tout dans un flux fluide.',
    
    // Forgot Password
    'auth.forgotPasswordLink': 'Mot de passe oublié?',
    'auth.forgotPassword': 'Mot de passe oublié?',
    'auth.forgotPasswordInfo': 'Entrez votre email et nous vous enverrons un lien pour réinitialiser votre mot de passe.',
    'auth.sendResetLink': 'Envoyer le Lien',
    'auth.backToLogin': 'Retour à la connexion',
    'auth.rememberPassword': 'Vous vous souvenez de votre mot de passe?',
    'auth.checkYourEmail': 'Vérifiez votre email',
    'auth.resetLinkSent': 'Lien de réinitialisation envoyé!',
    'auth.resetLinkSentTo': 'Nous avons envoyé un lien de réinitialisation à',
    'auth.didntReceiveEmail': 'Vous n\'avez pas reçu l\'email? Vérifiez vos spams ou réessayez.',
    'auth.tryAgain': 'Réessayer',
    'auth.resetRightTitle': 'Récupération de Mot de Passe',
    'auth.resetRightSubtitle': 'Nous vous aiderons à récupérer votre compte en toute sécurité.',
    'auth.resetError': 'Une erreur s\'est produite. Veuillez réessayer.',
    'auth.resetPassword': 'Réinitialisez votre mot de passe',
    'auth.resetPasswordInfo': 'Entrez votre nouveau mot de passe ci-dessous.',
    'auth.resettingFor': 'Réinitialisation pour',
    'auth.newPassword': 'Nouveau Mot de Passe',
    'auth.confirmPassword': 'Confirmer le Mot de Passe',
    'auth.passwordRequirements': 'Doit contenir au moins 6 caractères',
    'auth.passwordsDoNotMatch': 'Les mots de passe ne correspondent pas',
    'auth.passwordTooShort': 'Le mot de passe doit contenir au moins 6 caractères',
    'auth.resetPasswordBtn': 'Réinitialiser',
    'auth.passwordResetSuccess': 'Mot de passe réinitialisé!',
    'auth.passwordResetComplete': 'Réinitialisation Terminée',
    'auth.passwordResetCompleteInfo': 'Votre mot de passe a été réinitialisé. Vous pouvez maintenant vous connecter.',
    'auth.redirectingToLogin': 'Redirection vers la connexion...',
    'auth.signInNow': 'Se Connecter',
    'auth.verifyingToken': 'Vérification du lien...',
    'auth.tokenMissing': 'Lien Manquant',
    'auth.tokenMissingInfo': 'Aucun token trouvé. Veuillez demander un nouveau lien.',
    'auth.tokenExpired': 'Lien Expiré',
    'auth.tokenExpiredInfo': 'Ce lien a expiré. Veuillez en demander un nouveau.',
    'auth.tokenInvalid': 'Lien Invalide',
    'auth.tokenInvalidInfo': 'Ce lien est invalide. Veuillez en demander un nouveau.',
    'auth.requestNewLink': 'Demander un Nouveau Lien',
    'auth.secureReset': 'Réinitialisation Sécurisée',
    'auth.secureResetInfo': 'Votre lien est sécurisé et expire dans 1 heure.',
    
    // Email Verification
    'verify.bannerTitle': 'Veuillez vérifier votre email',
    'verify.bannerMessage': 'Vérifiez votre boîte de réception et cliquez sur le lien de vérification.',
    'verify.resendEmail': 'Renvoyer l\'Email',
    'verify.sending': 'Envoi...',
    'verify.emailSent': 'Email de vérification envoyé!',
    'verify.clickToVerify': 'Cliquez pour vérifier',
    'verify.verifying': 'Vérification de votre email...',
    'verify.success': 'Email vérifié avec succès!',
    'verify.successTitle': 'Email Vérifié!',
    'verify.successMessage': 'Votre email a été vérifié. Vous avez maintenant accès à toutes les fonctionnalités.',
    'verify.redirecting': 'Redirection vers le tableau de bord...',
    'verify.goToDashboard': 'Aller au Tableau de Bord',
    'verify.expiredTitle': 'Lien Expiré',
    'verify.expiredMessage': 'Ce lien a expiré. Veuillez en demander un nouveau.',
    'verify.resendLink': 'Renvoyer le Lien',
    'verify.resending': 'Envoi...',
    'verify.invalidTitle': 'Lien Invalide',
    'verify.invalidMessage': 'Ce lien est invalide ou a déjà été utilisé.',
    'verify.backToLogin': 'Retour à la Connexion',
    'verify.tokenMissing': 'Aucun token de vérification trouvé.',
    'verify.error': 'Une erreur s\'est produite. Veuillez réessayer.',
    'verify.resendError': 'Échec de l\'envoi. Veuillez réessayer.',
    'verify.alreadyVerified': 'Votre email est déjà vérifié!',
    'verify.noEmail': 'Aucune adresse email trouvée.',
    'verify.resent': 'Email de vérification envoyé!',
    'verify.resentTitle': 'Email Envoyé!',
    'verify.resentMessage': 'Un nouveau lien a été envoyé à votre email.',
    'verify.rightTitle': 'Vérification Email',
    'verify.rightSubtitle': 'Vérifiez votre email pour accéder à toutes les fonctionnalités.',
    
    // Dashboard
    'dashboard.welcome': 'Bienvenue',
    'dashboard.whatCreate': 'Que souhaitez-vous créer aujourd\'hui?',
    'dashboard.credits': 'Crédits Restants',
    'dashboard.plan': 'Plan Actuel',
    'dashboard.creations': 'Créations Totales',
    'dashboard.quickActions': 'Actions Rapides',
    'dashboard.recentCreations': 'Créations Récentes',
    'dashboard.noCreations': 'Pas encore de créations',
    'dashboard.startCreating': 'Commencer à Créer',
    'dashboard.tools': 'Outils IA',
    'dashboard.gallery': 'Galerie',
    'dashboard.settings': 'Paramètres',
    'dashboard.logout': 'Déconnexion',
    
    // Tools
    'tools.imageToPrompt': 'Image vers Prompt',
    'tools.imageToPromptDesc': 'Extrayez des prompts détaillés de n\'importe quelle image',
    'tools.promptEnhancer': 'Améliorateur de Prompts',
    'tools.promptEnhancerDesc': 'Améliorez et optimisez vos prompts',
    'tools.imageGenerator': 'Générateur d\'Images',
    'tools.imageGeneratorDesc': 'Créez des images avec l\'IA',
    'tools.mockupGenerator': 'Générateur de Mockups',
    'tools.mockupGeneratorDesc': 'Créez des mockups de produits',
    'tools.backgroundGenerator': 'Générateur d\'Arrière-plans',
    'tools.backgroundGeneratorDesc': 'Générez des arrière-plans personnalisés',
    'tools.workflow': 'Flux Complet',
    'tools.workflowDesc': 'Pipeline créatif complet',
    
    // FAQ Page
    'faq.title': 'Questions Fréquentes',
    'faq.subtitle': 'Réponses rapides aux questions courantes',
    'faq.stillHaveQuestions': 'Encore des questions?',
    'faq.hereToHelp': 'Nous sommes là pour vous aider',
    'faq.contactSupport': 'Contacter le Support',
    'faq.gettingStarted': 'Démarrage',
    'faq.billing': 'Facturation et Abonnement',
    'faq.creditsUsage': 'Crédits et Utilisation',
    'faq.features': 'Fonctionnalités',
    'faq.accountCancellation': 'Compte et Annulation',
    
    // Contact Page
    'contact.title': 'Contactez-nous',
    'contact.subtitle': 'Questions, problèmes ou commentaires? Nous sommes là.',
    'contact.name': 'Nom',
    'contact.namePlaceholder': 'Votre nom',
    'contact.email': 'E-mail',
    'contact.emailPlaceholder': 'vous@exemple.com',
    'contact.topic': 'Sujet',
    'contact.topicPlaceholder': 'Sélectionnez un sujet',
    'contact.message': 'Message',
    'contact.messagePlaceholder': 'Comment pouvons-nous vous aider?',
    'contact.send': 'Envoyer le Message',
    'contact.sending': 'Envoi...',
    'contact.directEmail': 'Ou écrivez-nous directement à',
    'contact.successTitle': 'Message Envoyé',
    'contact.successMessage': 'Nous vous répondrons dès que possible.',
    'contact.backToHome': 'Retour à l\'Accueil',
    'contact.sendAnother': 'Envoyer un Autre',
    'contact.topics.general': 'Question Générale',
    'contact.topics.technical': 'Problème Technique',
    'contact.topics.billing': 'Problème de Facturation',
    'contact.topics.feature': 'Demande de Fonctionnalité',
    'contact.topics.feedback': 'Commentaires',
    
    // Common
    'common.upload': 'Télécharger',
    'common.generate': 'Générer',
    'common.download': 'Télécharger',
    'common.share': 'Partager',
    'common.delete': 'Supprimer',
    'common.cancel': 'Annuler',
    'common.save': 'Enregistrer',
    'common.loading': 'Chargement...',
    'common.credits': 'crédits',
    'common.error': 'Erreur',
    'common.success': 'Succès',
  },
  de: {
    // Navigation
    'nav.features': 'Funktionen',
    'nav.pricing': 'Preise',
    'nav.faq': 'FAQ',
    'nav.contact': 'Kontakt',
    'nav.login': 'Anmelden',
    'nav.getStarted': 'Kostenlos testen',
    'nav.dashboard': 'Dashboard',
    'nav.back': 'Zurück',
    'nav.backToHome': 'Zurück zur Startseite',
    
    // Hero
    'hero.title1': 'Erstellen Sie Mockups, Prompts und Designs in',
    'hero.title2': 'Einem Workflow',
    'hero.subtitle': 'Laden Sie ein Bild hoch. Generieren Sie verkaufsstarke Prompts. Erstellen Sie realistische Mockups und Hintergründe ohne Tool-Wechsel.',
    'hero.cta': 'Kostenlos testen',
    'hero.seeFeatures': 'Funktionen ansehen',
    'hero.trial': '7 Tage kostenlos • 120 Credits • Jederzeit kündbar',
    
    // Features
    'features.title': 'Alles was Sie brauchen',
    'features.subtitle': 'Sechs KI-Tools in einer Plattform',
    'features.imageToPrompt': 'Bild zu Prompt',
    'features.imageToPromptDesc': 'Laden Sie ein Bild hoch und erhalten Sie detaillierte Prompts',
    'features.promptEnhancer': 'Prompt-Verbesserer',
    'features.promptEnhancerDesc': 'Verwandeln Sie einfache Prompts in hochwertige Anweisungen',
    'features.imageGenerator': 'KI-Bildgenerator',
    'features.imageGeneratorDesc': 'Generieren Sie beeindruckende Produktbilder und Sticker',
    'features.mockupGenerator': 'Mockup-Generator',
    'features.mockupGeneratorDesc': 'Erstellen Sie realistische Mockups mit Lifestyle-Hintergründen',
    'features.backgroundGenerator': 'Hintergrund-Generator',
    'features.backgroundGeneratorDesc': 'Generieren oder ersetzen Sie Hintergründe für Ihre Produkte',
    'features.workflow': 'Workflow-System',
    'features.workflowDesc': 'Verbinden Sie alle Tools in einem nahtlosen kreativen Fluss',
    
    // Showcase
    'showcase.title': 'Sehen Sie, was Sie erstellen können',
    'showcase.subtitle': 'Generieren Sie Prompts, Designs und Mockups in Sekunden.',
    'showcase.aiPowered': 'KI-gestützte Ergebnisse',
    'showcase.upload': 'Hochladen',
    'showcase.aiMagic': 'KI-Magie',
    'showcase.result': 'Ergebnis',
    'showcase.bottomText': 'Dies ersetzt mehrere Tools. Eine Plattform, endlose Möglichkeiten.',
    
    // Pricing
    'pricing.title': 'Einfache, transparente Preise',
    'pricing.subtitle': 'Starten Sie mit einer kostenlosen Testversion. Upgraden Sie, wenn Sie bereit sind.',
    'pricing.mostPopular': 'Beliebteste',
    'pricing.perMonth': '/Monat',
    'pricing.credits': 'Credits/Monat',
    'pricing.getStarted': 'Loslegen',
    
    // CTA
    'cta.title': 'Bereit, Ihren Workflow zu optimieren?',
    'cta.subtitle': 'Schließen Sie sich Tausenden von Kreativen an, die jede Woche Stunden sparen',
    
    // Footer
    'footer.rights': '© 2026 MockupPro. Alle Rechte vorbehalten.',
    
    // Auth
    'auth.createAccount': 'Erstellen Sie Ihr Konto',
    'auth.welcomeBack': 'Willkommen zurück',
    'auth.trialInfo': 'Erstellen Sie unbegrenzte Mockups für Ihre Etsy-Angebote',
    'auth.signInInfo': 'Melden Sie sich an, um auf Ihr Dashboard zuzugreifen',
    'auth.firstName': 'Vorname',
    'auth.firstNamePlaceholder': 'Max',
    'auth.lastName': 'Nachname',
    'auth.lastNamePlaceholder': 'Mustermann',
    'auth.name': 'Name',
    'auth.namePlaceholder': 'Ihr Name',
    'auth.email': 'E-Mail',
    'auth.emailPlaceholder': 'sie@beispiel.com',
    'auth.password': 'Passwort',
    'auth.startTrial': 'Kostenlose Testversion starten',
    'auth.signIn': 'Anmelden',
    'auth.processing': 'Verarbeitung...',
    'auth.haveAccount': 'Haben Sie bereits ein Konto?',
    'auth.noAccount': 'Noch kein Konto?',
    'auth.signInLink': 'Anmelden',
    'auth.startTrialLink': 'Kostenlos testen',
    'auth.rightTitle': 'Eine Plattform, endlose Möglichkeiten',
    'auth.rightSubtitle': 'Generieren Sie Prompts, erstellen Sie Bilder, designen Sie Mockups—alles in einem nahtlosen Workflow.',
    
    // Forgot Password
    'auth.forgotPasswordLink': 'Passwort vergessen?',
    'auth.forgotPassword': 'Passwort vergessen?',
    'auth.forgotPasswordInfo': 'Geben Sie Ihre E-Mail ein und wir senden Ihnen einen Link zum Zurücksetzen.',
    'auth.sendResetLink': 'Link Senden',
    'auth.backToLogin': 'Zurück zur Anmeldung',
    'auth.rememberPassword': 'Erinnern Sie sich an Ihr Passwort?',
    'auth.checkYourEmail': 'Prüfen Sie Ihre E-Mail',
    'auth.resetLinkSent': 'Zurücksetzungslink gesendet!',
    'auth.resetLinkSentTo': 'Wir haben einen Zurücksetzungslink gesendet an',
    'auth.didntReceiveEmail': 'Keine E-Mail erhalten? Prüfen Sie Ihren Spam-Ordner oder versuchen Sie es erneut.',
    'auth.tryAgain': 'Erneut Versuchen',
    'auth.resetRightTitle': 'Passwort-Wiederherstellung',
    'auth.resetRightSubtitle': 'Wir helfen Ihnen, sicher wieder in Ihr Konto zu gelangen.',
    'auth.resetError': 'Etwas ist schief gelaufen. Bitte versuchen Sie es erneut.',
    'auth.resetPassword': 'Passwort zurücksetzen',
    'auth.resetPasswordInfo': 'Geben Sie Ihr neues Passwort ein.',
    'auth.resettingFor': 'Passwort zurücksetzen für',
    'auth.newPassword': 'Neues Passwort',
    'auth.confirmPassword': 'Passwort Bestätigen',
    'auth.passwordRequirements': 'Mindestens 6 Zeichen',
    'auth.passwordsDoNotMatch': 'Passwörter stimmen nicht überein',
    'auth.passwordTooShort': 'Passwort muss mindestens 6 Zeichen haben',
    'auth.resetPasswordBtn': 'Passwort Zurücksetzen',
    'auth.passwordResetSuccess': 'Passwort erfolgreich zurückgesetzt!',
    'auth.passwordResetComplete': 'Zurücksetzen Abgeschlossen',
    'auth.passwordResetCompleteInfo': 'Ihr Passwort wurde zurückgesetzt. Sie können sich jetzt anmelden.',
    'auth.redirectingToLogin': 'Weiterleitung zur Anmeldung...',
    'auth.signInNow': 'Jetzt Anmelden',
    'auth.verifyingToken': 'Link wird überprüft...',
    'auth.tokenMissing': 'Link Fehlt',
    'auth.tokenMissingInfo': 'Kein Token gefunden. Bitte fordern Sie einen neuen Link an.',
    'auth.tokenExpired': 'Link Abgelaufen',
    'auth.tokenExpiredInfo': 'Dieser Link ist abgelaufen. Bitte fordern Sie einen neuen an.',
    'auth.tokenInvalid': 'Ungültiger Link',
    'auth.tokenInvalidInfo': 'Dieser Link ist ungültig. Bitte fordern Sie einen neuen an.',
    'auth.requestNewLink': 'Neuen Link Anfordern',
    'auth.secureReset': 'Sicheres Zurücksetzen',
    'auth.secureResetInfo': 'Ihr Link ist sicher und läuft in 1 Stunde ab.',
    
    // Email Verification
    'verify.bannerTitle': 'Bitte bestätigen Sie Ihre E-Mail',
    'verify.bannerMessage': 'Prüfen Sie Ihren Posteingang und klicken Sie auf den Bestätigungslink.',
    'verify.resendEmail': 'E-Mail erneut senden',
    'verify.sending': 'Senden...',
    'verify.emailSent': 'Bestätigungs-E-Mail gesendet!',
    'verify.clickToVerify': 'Zum Bestätigen klicken',
    'verify.verifying': 'E-Mail wird überprüft...',
    'verify.success': 'E-Mail erfolgreich bestätigt!',
    'verify.successTitle': 'E-Mail Bestätigt!',
    'verify.successMessage': 'Ihre E-Mail wurde bestätigt. Sie haben nun vollen Zugriff.',
    'verify.redirecting': 'Weiterleitung zum Dashboard...',
    'verify.goToDashboard': 'Zum Dashboard',
    'verify.expiredTitle': 'Link Abgelaufen',
    'verify.expiredMessage': 'Dieser Link ist abgelaufen. Bitte fordern Sie einen neuen an.',
    'verify.resendLink': 'Link erneut senden',
    'verify.resending': 'Senden...',
    'verify.invalidTitle': 'Ungültiger Link',
    'verify.invalidMessage': 'Dieser Link ist ungültig oder wurde bereits verwendet.',
    'verify.backToLogin': 'Zurück zum Login',
    'verify.tokenMissing': 'Kein Bestätigungstoken gefunden.',
    'verify.error': 'Etwas ist schief gelaufen. Bitte erneut versuchen.',
    'verify.resendError': 'Fehler beim Senden. Bitte erneut versuchen.',
    'verify.alreadyVerified': 'Ihre E-Mail ist bereits bestätigt!',
    'verify.noEmail': 'Keine E-Mail-Adresse gefunden.',
    'verify.resent': 'Bestätigungs-E-Mail gesendet!',
    'verify.resentTitle': 'E-Mail Gesendet!',
    'verify.resentMessage': 'Ein neuer Link wurde an Ihre E-Mail gesendet.',
    'verify.rightTitle': 'E-Mail-Bestätigung',
    'verify.rightSubtitle': 'Bestätigen Sie Ihre E-Mail für vollen Zugriff.',
    
    // Dashboard
    'dashboard.welcome': 'Willkommen zurück',
    'dashboard.whatCreate': 'Was möchten Sie heute erstellen?',
    'dashboard.credits': 'Verbleibende Credits',
    'dashboard.plan': 'Aktueller Plan',
    'dashboard.creations': 'Gesamte Kreationen',
    'dashboard.quickActions': 'Schnellaktionen',
    'dashboard.recentCreations': 'Neueste Kreationen',
    'dashboard.noCreations': 'Noch keine Kreationen',
    'dashboard.startCreating': 'Mit dem Erstellen beginnen',
    'dashboard.tools': 'KI-Tools',
    'dashboard.gallery': 'Galerie',
    'dashboard.settings': 'Einstellungen',
    'dashboard.logout': 'Abmelden',
    
    // Tools
    'tools.imageToPrompt': 'Bild zu Prompt',
    'tools.imageToPromptDesc': 'Extrahieren Sie detaillierte Prompts aus jedem Bild',
    'tools.promptEnhancer': 'Prompt-Verbesserer',
    'tools.promptEnhancerDesc': 'Verbessern und optimieren Sie Ihre Prompts',
    'tools.imageGenerator': 'Bildgenerator',
    'tools.imageGeneratorDesc': 'Erstellen Sie KI-generierte Bilder',
    'tools.mockupGenerator': 'Mockup-Generator',
    'tools.mockupGeneratorDesc': 'Erstellen Sie Produkt-Mockups',
    'tools.backgroundGenerator': 'Hintergrund-Generator',
    'tools.backgroundGeneratorDesc': 'Generieren Sie benutzerdefinierte Hintergründe',
    'tools.workflow': 'Vollständiger Workflow',
    'tools.workflowDesc': 'Komplette kreative Pipeline',
    
    // FAQ Page
    'faq.title': 'Häufig gestellte Fragen',
    'faq.subtitle': 'Schnelle Antworten auf häufige Fragen',
    'faq.stillHaveQuestions': 'Noch Fragen?',
    'faq.hereToHelp': 'Wir sind hier, um zu helfen',
    'faq.contactSupport': 'Support kontaktieren',
    'faq.gettingStarted': 'Erste Schritte',
    'faq.billing': 'Abrechnung & Abonnement',
    'faq.creditsUsage': 'Credits & Nutzung',
    'faq.features': 'Funktionen',
    'faq.accountCancellation': 'Konto & Kündigung',
    
    // Contact Page
    'contact.title': 'Kontaktieren Sie uns',
    'contact.subtitle': 'Fragen, Probleme oder Feedback? Wir sind hier.',
    'contact.name': 'Name',
    'contact.namePlaceholder': 'Ihr Name',
    'contact.email': 'E-Mail',
    'contact.emailPlaceholder': 'sie@beispiel.com',
    'contact.topic': 'Thema',
    'contact.topicPlaceholder': 'Wählen Sie ein Thema',
    'contact.message': 'Nachricht',
    'contact.messagePlaceholder': 'Wie können wir helfen?',
    'contact.send': 'Nachricht senden',
    'contact.sending': 'Wird gesendet...',
    'contact.directEmail': 'Oder schreiben Sie uns direkt an',
    'contact.successTitle': 'Nachricht gesendet',
    'contact.successMessage': 'Wir werden uns so schnell wie möglich bei Ihnen melden.',
    'contact.backToHome': 'Zurück zur Startseite',
    'contact.sendAnother': 'Eine weitere senden',
    'contact.topics.general': 'Allgemeine Frage',
    'contact.topics.technical': 'Technisches Problem',
    'contact.topics.billing': 'Abrechnungsproblem',
    'contact.topics.feature': 'Funktionsanfrage',
    'contact.topics.feedback': 'Feedback',
    
    // Common
    'common.upload': 'Hochladen',
    'common.generate': 'Generieren',
    'common.download': 'Herunterladen',
    'common.share': 'Teilen',
    'common.delete': 'Löschen',
    'common.cancel': 'Abbrechen',
    'common.save': 'Speichern',
    'common.loading': 'Wird geladen...',
    'common.credits': 'Credits',
    'common.error': 'Fehler',
    'common.success': 'Erfolg',
  },
  pt: {
    // Navigation
    'nav.features': 'Recursos',
    'nav.pricing': 'Preços',
    'nav.faq': 'FAQ',
    'nav.contact': 'Contato',
    'nav.login': 'Entrar',
    'nav.getStarted': 'Teste Grátis',
    'nav.dashboard': 'Painel',
    'nav.back': 'Voltar',
    'nav.backToHome': 'Voltar ao início',
    
    // Hero
    'hero.title1': 'Crie Mockups, Prompts e Designs em',
    'hero.title2': 'Um Único Fluxo',
    'hero.subtitle': 'Envie qualquer imagem. Gere prompts de alta conversão. Crie mockups e fundos realistas sem trocar de ferramenta.',
    'hero.cta': 'Teste Grátis',
    'hero.seeFeatures': 'Ver Recursos',
    'hero.trial': '7 dias grátis • 120 créditos • Cancele quando quiser',
    
    // Features
    'features.title': 'Tudo o que você precisa',
    'features.subtitle': 'Seis ferramentas IA em uma plataforma',
    'features.imageToPrompt': 'Imagem para Prompt',
    'features.imageToPromptDesc': 'Envie qualquer imagem e obtenha prompts detalhados',
    'features.promptEnhancer': 'Melhorador de Prompts',
    'features.promptEnhancerDesc': 'Transforme prompts básicos em instruções de alta qualidade',
    'features.imageGenerator': 'Gerador de Imagens IA',
    'features.imageGeneratorDesc': 'Gere impressionantes imagens de produtos e stickers',
    'features.mockupGenerator': 'Gerador de Mockups',
    'features.mockupGeneratorDesc': 'Crie mockups realistas com fundos lifestyle',
    'features.backgroundGenerator': 'Gerador de Fundos',
    'features.backgroundGeneratorDesc': 'Gere ou substitua fundos para seu produto',
    'features.workflow': 'Sistema de Fluxo',
    'features.workflowDesc': 'Conecte todas as ferramentas em um fluxo criativo fluido',
    
    // Showcase
    'showcase.title': 'Veja o que você pode criar',
    'showcase.subtitle': 'Gere prompts, designs e mockups realistas em segundos.',
    'showcase.aiPowered': 'Resultados com IA',
    'showcase.upload': 'Enviar',
    'showcase.aiMagic': 'Magia IA',
    'showcase.result': 'Resultado',
    'showcase.bottomText': 'Isso substitui múltiplas ferramentas. Uma plataforma, possibilidades infinitas.',
    
    // Pricing
    'pricing.title': 'Preços simples e transparentes',
    'pricing.subtitle': 'Comece com um teste gratuito. Atualize quando estiver pronto.',
    'pricing.mostPopular': 'Mais Popular',
    'pricing.perMonth': '/mês',
    'pricing.credits': 'créditos/mês',
    'pricing.getStarted': 'Começar',
    
    // CTA
    'cta.title': 'Pronto para otimizar seu fluxo de trabalho?',
    'cta.subtitle': 'Junte-se a milhares de criadores economizando horas toda semana',
    
    // Footer
    'footer.rights': '© 2026 MockupPro. Todos os direitos reservados.',
    
    // Auth
    'auth.createAccount': 'Crie sua conta',
    'auth.welcomeBack': 'Bem-vindo de volta',
    'auth.trialInfo': 'Crie mockups ilimitados para suas listagens Etsy',
    'auth.signInInfo': 'Entre para acessar seu painel',
    'auth.firstName': 'Nome',
    'auth.firstNamePlaceholder': 'João',
    'auth.lastName': 'Sobrenome',
    'auth.lastNamePlaceholder': 'Silva',
    'auth.name': 'Nome',
    'auth.namePlaceholder': 'Seu nome',
    'auth.email': 'E-mail',
    'auth.emailPlaceholder': 'voce@exemplo.com',
    'auth.password': 'Senha',
    'auth.startTrial': 'Iniciar Teste Grátis',
    'auth.signIn': 'Entrar',
    'auth.processing': 'Processando...',
    'auth.haveAccount': 'Já tem uma conta?',
    'auth.noAccount': 'Não tem uma conta?',
    'auth.signInLink': 'Entrar',
    'auth.startTrialLink': 'Teste grátis',
    'auth.rightTitle': 'Uma plataforma, possibilidades infinitas',
    'auth.rightSubtitle': 'Gere prompts, crie imagens, designe mockups—tudo em um fluxo perfeito.',
    
    // Forgot Password
    'auth.forgotPasswordLink': 'Esqueceu a senha?',
    'auth.forgotPassword': 'Esqueceu sua senha?',
    'auth.forgotPasswordInfo': 'Digite seu e-mail e enviaremos um link para redefinir sua senha.',
    'auth.sendResetLink': 'Enviar Link',
    'auth.backToLogin': 'Voltar ao login',
    'auth.rememberPassword': 'Lembra da sua senha?',
    'auth.checkYourEmail': 'Verifique seu e-mail',
    'auth.resetLinkSent': 'Link de redefinição enviado!',
    'auth.resetLinkSentTo': 'Enviamos um link de redefinição para',
    'auth.didntReceiveEmail': 'Não recebeu o e-mail? Verifique sua pasta de spam ou tente novamente.',
    'auth.tryAgain': 'Tentar Novamente',
    'auth.resetRightTitle': 'Recuperação de Senha',
    'auth.resetRightSubtitle': 'Vamos ajudá-lo a recuperar sua conta com segurança.',
    'auth.resetError': 'Algo deu errado. Por favor, tente novamente.',
    'auth.resetPassword': 'Redefinir sua senha',
    'auth.resetPasswordInfo': 'Digite sua nova senha abaixo.',
    'auth.resettingFor': 'Redefinindo senha para',
    'auth.newPassword': 'Nova Senha',
    'auth.confirmPassword': 'Confirmar Senha',
    'auth.passwordRequirements': 'Deve ter pelo menos 6 caracteres',
    'auth.passwordsDoNotMatch': 'As senhas não coincidem',
    'auth.passwordTooShort': 'A senha deve ter pelo menos 6 caracteres',
    'auth.resetPasswordBtn': 'Redefinir Senha',
    'auth.passwordResetSuccess': 'Senha redefinida com sucesso!',
    'auth.passwordResetComplete': 'Redefinição Completa',
    'auth.passwordResetCompleteInfo': 'Sua senha foi redefinida. Você já pode entrar com sua nova senha.',
    'auth.redirectingToLogin': 'Redirecionando para o login...',
    'auth.signInNow': 'Entrar Agora',
    'auth.verifyingToken': 'Verificando link...',
    'auth.tokenMissing': 'Link Ausente',
    'auth.tokenMissingInfo': 'Nenhum token encontrado. Por favor, solicite um novo link.',
    'auth.tokenExpired': 'Link Expirado',
    'auth.tokenExpiredInfo': 'Este link expirou. Por favor, solicite um novo.',
    'auth.tokenInvalid': 'Link Inválido',
    'auth.tokenInvalidInfo': 'Este link é inválido. Por favor, solicite um novo.',
    'auth.requestNewLink': 'Solicitar Novo Link',
    'auth.secureReset': 'Redefinição Segura',
    'auth.secureResetInfo': 'Seu link é seguro e expira em 1 hora.',
    
    // Email Verification
    'verify.bannerTitle': 'Por favor verifique seu e-mail',
    'verify.bannerMessage': 'Verifique sua caixa de entrada e clique no link de verificação.',
    'verify.resendEmail': 'Reenviar E-mail',
    'verify.sending': 'Enviando...',
    'verify.emailSent': 'E-mail de verificação enviado!',
    'verify.clickToVerify': 'Clique para verificar',
    'verify.verifying': 'Verificando seu e-mail...',
    'verify.success': 'E-mail verificado com sucesso!',
    'verify.successTitle': 'E-mail Verificado!',
    'verify.successMessage': 'Seu e-mail foi verificado. Você agora tem acesso total.',
    'verify.redirecting': 'Redirecionando para o painel...',
    'verify.goToDashboard': 'Ir para o Painel',
    'verify.expiredTitle': 'Link Expirado',
    'verify.expiredMessage': 'Este link expirou. Por favor solicite um novo.',
    'verify.resendLink': 'Reenviar Link',
    'verify.resending': 'Enviando...',
    'verify.invalidTitle': 'Link Inválido',
    'verify.invalidMessage': 'Este link é inválido ou já foi usado.',
    'verify.backToLogin': 'Voltar ao Login',
    'verify.tokenMissing': 'Token de verificação não encontrado.',
    'verify.error': 'Algo deu errado. Por favor tente novamente.',
    'verify.resendError': 'Falha ao enviar. Por favor tente novamente.',
    'verify.alreadyVerified': 'Seu e-mail já está verificado!',
    'verify.noEmail': 'Nenhum e-mail encontrado.',
    'verify.resent': 'E-mail de verificação enviado!',
    'verify.resentTitle': 'E-mail Enviado!',
    'verify.resentMessage': 'Um novo link foi enviado para seu e-mail.',
    'verify.rightTitle': 'Verificação de E-mail',
    'verify.rightSubtitle': 'Verifique seu e-mail para acesso total.',
    
    // Dashboard
    'dashboard.welcome': 'Bem-vindo de volta',
    'dashboard.whatCreate': 'O que você gostaria de criar hoje?',
    'dashboard.credits': 'Créditos Restantes',
    'dashboard.plan': 'Plano Atual',
    'dashboard.creations': 'Criações Totais',
    'dashboard.quickActions': 'Ações Rápidas',
    'dashboard.recentCreations': 'Criações Recentes',
    'dashboard.noCreations': 'Ainda sem criações',
    'dashboard.startCreating': 'Começar a Criar',
    'dashboard.tools': 'Ferramentas IA',
    'dashboard.gallery': 'Galeria',
    'dashboard.settings': 'Configurações',
    'dashboard.logout': 'Sair',
    
    // Tools
    'tools.imageToPrompt': 'Imagem para Prompt',
    'tools.imageToPromptDesc': 'Extraia prompts detalhados de qualquer imagem',
    'tools.promptEnhancer': 'Melhorador de Prompts',
    'tools.promptEnhancerDesc': 'Melhore e otimize seus prompts',
    'tools.imageGenerator': 'Gerador de Imagens',
    'tools.imageGeneratorDesc': 'Crie imagens com IA',
    'tools.mockupGenerator': 'Gerador de Mockups',
    'tools.mockupGeneratorDesc': 'Crie mockups de produtos',
    'tools.backgroundGenerator': 'Gerador de Fundos',
    'tools.backgroundGeneratorDesc': 'Gere fundos personalizados',
    'tools.workflow': 'Fluxo Completo',
    'tools.workflowDesc': 'Pipeline criativo completo',
    
    // FAQ Page
    'faq.title': 'Perguntas Frequentes',
    'faq.subtitle': 'Respostas rápidas para perguntas comuns',
    'faq.stillHaveQuestions': 'Ainda tem perguntas?',
    'faq.hereToHelp': 'Estamos aqui para ajudar',
    'faq.contactSupport': 'Contatar Suporte',
    'faq.gettingStarted': 'Começando',
    'faq.billing': 'Faturamento e Assinatura',
    'faq.creditsUsage': 'Créditos e Uso',
    'faq.features': 'Recursos',
    'faq.accountCancellation': 'Conta e Cancelamento',
    
    // Contact Page
    'contact.title': 'Fale Conosco',
    'contact.subtitle': 'Perguntas, problemas ou feedback? Estamos aqui.',
    'contact.name': 'Nome',
    'contact.namePlaceholder': 'Seu nome',
    'contact.email': 'E-mail',
    'contact.emailPlaceholder': 'voce@exemplo.com',
    'contact.topic': 'Assunto',
    'contact.topicPlaceholder': 'Selecione um assunto',
    'contact.message': 'Mensagem',
    'contact.messagePlaceholder': 'Como podemos ajudar?',
    'contact.send': 'Enviar Mensagem',
    'contact.sending': 'Enviando...',
    'contact.directEmail': 'Ou envie um e-mail diretamente para',
    'contact.successTitle': 'Mensagem Enviada',
    'contact.successMessage': 'Responderemos o mais rápido possível.',
    'contact.backToHome': 'Voltar ao Início',
    'contact.sendAnother': 'Enviar Outra',
    'contact.topics.general': 'Pergunta Geral',
    'contact.topics.technical': 'Problema Técnico',
    'contact.topics.billing': 'Problema de Faturamento',
    'contact.topics.feature': 'Solicitação de Recurso',
    'contact.topics.feedback': 'Feedback',
    
    // Common
    'common.upload': 'Enviar',
    'common.generate': 'Gerar',
    'common.download': 'Baixar',
    'common.share': 'Compartilhar',
    'common.delete': 'Excluir',
    'common.cancel': 'Cancelar',
    'common.save': 'Salvar',
    'common.loading': 'Carregando...',
    'common.credits': 'créditos',
    'common.error': 'Erro',
    'common.success': 'Sucesso',
  },
  it: {
    // Navigation
    'nav.features': 'Funzionalità',
    'nav.pricing': 'Prezzi',
    'nav.faq': 'FAQ',
    'nav.contact': 'Contatto',
    'nav.login': 'Accedi',
    'nav.getStarted': 'Prova Gratuita',
    'nav.dashboard': 'Dashboard',
    'nav.back': 'Indietro',
    'nav.backToHome': 'Torna alla home',
    
    // Hero
    'hero.title1': 'Crea Mockup, Prompt e Design in',
    'hero.title2': 'Un Unico Workflow',
    'hero.subtitle': 'Carica un\'immagine. Genera prompt ad alta conversione. Crea mockup e sfondi realistici senza cambiare strumento.',
    'hero.cta': 'Prova Gratuita',
    'hero.seeFeatures': 'Vedi Funzionalità',
    'hero.trial': '7 giorni gratis • 120 crediti • Cancella quando vuoi',
    
    // Features
    'features.title': 'Tutto ciò di cui hai bisogno',
    'features.subtitle': 'Sei strumenti IA in una piattaforma unificata',
    'features.imageToPrompt': 'Immagine a Prompt',
    'features.imageToPromptDesc': 'Carica qualsiasi immagine e ottieni prompt dettagliati',
    'features.promptEnhancer': 'Miglioratore di Prompt',
    'features.promptEnhancerDesc': 'Trasforma prompt semplici in istruzioni di alta qualità',
    'features.imageGenerator': 'Generatore di Immagini IA',
    'features.imageGeneratorDesc': 'Genera straordinarie immagini di prodotti e sticker',
    'features.mockupGenerator': 'Generatore di Mockup',
    'features.mockupGeneratorDesc': 'Crea mockup realistici con sfondi lifestyle',
    'features.backgroundGenerator': 'Generatore di Sfondi',
    'features.backgroundGeneratorDesc': 'Genera o sostituisci gli sfondi per i tuoi prodotti',
    'features.workflow': 'Sistema di Flusso',
    'features.workflowDesc': 'Collega tutti gli strumenti in un flusso creativo fluido',
    
    // Showcase
    'showcase.title': 'Guarda cosa puoi creare',
    'showcase.subtitle': 'Genera prompt, design e mockup realistici in pochi secondi.',
    'showcase.aiPowered': 'Risultati IA',
    'showcase.upload': 'Carica',
    'showcase.aiMagic': 'Magia IA',
    'showcase.result': 'Risultato',
    'showcase.bottomText': 'Questo sostituisce più strumenti. Una piattaforma, possibilità infinite.',
    
    // Pricing
    'pricing.title': 'Prezzi semplici e trasparenti',
    'pricing.subtitle': 'Inizia con una prova gratuita. Aggiorna quando sei pronto.',
    'pricing.mostPopular': 'Più Popolare',
    'pricing.perMonth': '/mese',
    'pricing.credits': 'crediti/mese',
    'pricing.getStarted': 'Inizia',
    
    // CTA
    'cta.title': 'Pronto a ottimizzare il tuo flusso di lavoro?',
    'cta.subtitle': 'Unisciti a migliaia di creatori che risparmiano ore ogni settimana',
    
    // Footer
    'footer.rights': '© 2026 MockupPro. Tutti i diritti riservati.',
    
    // Auth
    'auth.createAccount': 'Crea il tuo account',
    'auth.welcomeBack': 'Bentornato',
    'auth.trialInfo': 'Crea mockup illimitati per le tue inserzioni Etsy',
    'auth.signInInfo': 'Accedi per accedere alla tua dashboard',
    'auth.firstName': 'Nome',
    'auth.firstNamePlaceholder': 'Marco',
    'auth.lastName': 'Cognome',
    'auth.lastNamePlaceholder': 'Rossi',
    'auth.name': 'Nome',
    'auth.namePlaceholder': 'Il tuo nome',
    'auth.email': 'E-mail',
    'auth.emailPlaceholder': 'tu@esempio.com',
    'auth.password': 'Password',
    'auth.startTrial': 'Inizia Prova Gratuita',
    'auth.signIn': 'Accedi',
    'auth.processing': 'Elaborazione...',
    'auth.haveAccount': 'Hai già un account?',
    'auth.noAccount': 'Non hai un account?',
    'auth.signInLink': 'Accedi',
    'auth.startTrialLink': 'Prova gratuita',
    'auth.rightTitle': 'Una piattaforma, possibilità infinite',
    'auth.rightSubtitle': 'Genera prompt, crea immagini, progetta mockup—tutto in un flusso fluido.',
    
    // Forgot Password
    'auth.forgotPasswordLink': 'Password dimenticata?',
    'auth.forgotPassword': 'Password dimenticata?',
    'auth.forgotPasswordInfo': 'Inserisci la tua e-mail e ti invieremo un link per reimpostare la password.',
    'auth.sendResetLink': 'Invia Link',
    'auth.backToLogin': 'Torna al login',
    'auth.rememberPassword': 'Ricordi la tua password?',
    'auth.checkYourEmail': 'Controlla la tua e-mail',
    'auth.resetLinkSent': 'Link di reimpostazione inviato!',
    'auth.resetLinkSentTo': 'Abbiamo inviato un link di reimpostazione a',
    'auth.didntReceiveEmail': 'Non hai ricevuto l\'e-mail? Controlla la cartella spam o riprova.',
    'auth.tryAgain': 'Riprova',
    'auth.resetRightTitle': 'Recupero Password',
    'auth.resetRightSubtitle': 'Ti aiuteremo a recuperare il tuo account in sicurezza.',
    'auth.resetError': 'Qualcosa è andato storto. Per favore riprova.',
    'auth.resetPassword': 'Reimposta la tua password',
    'auth.resetPasswordInfo': 'Inserisci la tua nuova password qui sotto.',
    'auth.resettingFor': 'Reimpostazione password per',
    'auth.newPassword': 'Nuova Password',
    'auth.confirmPassword': 'Conferma Password',
    'auth.passwordRequirements': 'Deve avere almeno 6 caratteri',
    'auth.passwordsDoNotMatch': 'Le password non corrispondono',
    'auth.passwordTooShort': 'La password deve avere almeno 6 caratteri',
    'auth.resetPasswordBtn': 'Reimposta Password',
    'auth.passwordResetSuccess': 'Password reimpostata con successo!',
    'auth.passwordResetComplete': 'Reimpostazione Completata',
    'auth.passwordResetCompleteInfo': 'La tua password è stata reimpostata. Ora puoi accedere con la nuova password.',
    'auth.redirectingToLogin': 'Reindirizzamento al login...',
    'auth.signInNow': 'Accedi Ora',
    'auth.verifyingToken': 'Verifica del link...',
    'auth.tokenMissing': 'Link Mancante',
    'auth.tokenMissingInfo': 'Nessun token trovato. Per favore richiedi un nuovo link.',
    'auth.tokenExpired': 'Link Scaduto',
    'auth.tokenExpiredInfo': 'Questo link è scaduto. Per favore richiedine uno nuovo.',
    'auth.tokenInvalid': 'Link Non Valido',
    'auth.tokenInvalidInfo': 'Questo link non è valido. Per favore richiedine uno nuovo.',
    'auth.requestNewLink': 'Richiedi Nuovo Link',
    'auth.secureReset': 'Reimpostazione Sicura',
    'auth.secureResetInfo': 'Il tuo link è sicuro e scade tra 1 ora.',
    
    // Email Verification
    'verify.bannerTitle': 'Verifica la tua email',
    'verify.bannerMessage': 'Controlla la tua posta e clicca sul link di verifica.',
    'verify.resendEmail': 'Reinvia Email',
    'verify.sending': 'Invio...',
    'verify.emailSent': 'Email di verifica inviata!',
    'verify.clickToVerify': 'Clicca per verificare',
    'verify.verifying': 'Verifica della tua email...',
    'verify.success': 'Email verificata con successo!',
    'verify.successTitle': 'Email Verificata!',
    'verify.successMessage': 'La tua email è stata verificata. Ora hai accesso completo.',
    'verify.redirecting': 'Reindirizzamento alla dashboard...',
    'verify.goToDashboard': 'Vai alla Dashboard',
    'verify.expiredTitle': 'Link Scaduto',
    'verify.expiredMessage': 'Questo link è scaduto. Per favore richiedine uno nuovo.',
    'verify.resendLink': 'Reinvia Link',
    'verify.resending': 'Invio...',
    'verify.invalidTitle': 'Link Non Valido',
    'verify.invalidMessage': 'Questo link non è valido o è già stato usato.',
    'verify.backToLogin': 'Torna al Login',
    'verify.tokenMissing': 'Token di verifica non trovato.',
    'verify.error': 'Qualcosa è andato storto. Per favore riprova.',
    'verify.resendError': 'Invio fallito. Per favore riprova.',
    'verify.alreadyVerified': 'La tua email è già verificata!',
    'verify.noEmail': 'Nessun indirizzo email trovato.',
    'verify.resent': 'Email di verifica inviata!',
    'verify.resentTitle': 'Email Inviata!',
    'verify.resentMessage': 'Un nuovo link è stato inviato alla tua email.',
    'verify.rightTitle': 'Verifica Email',
    'verify.rightSubtitle': 'Verifica la tua email per accesso completo.',
    
    // Dashboard
    'dashboard.welcome': 'Bentornato',
    'dashboard.whatCreate': 'Cosa vorresti creare oggi?',
    'dashboard.credits': 'Crediti Rimanenti',
    'dashboard.plan': 'Piano Attuale',
    'dashboard.creations': 'Creazioni Totali',
    'dashboard.quickActions': 'Azioni Rapide',
    'dashboard.recentCreations': 'Creazioni Recenti',
    'dashboard.noCreations': 'Ancora nessuna creazione',
    'dashboard.startCreating': 'Inizia a Creare',
    'dashboard.tools': 'Strumenti IA',
    'dashboard.gallery': 'Galleria',
    'dashboard.settings': 'Impostazioni',
    'dashboard.logout': 'Esci',
    
    // Tools
    'tools.imageToPrompt': 'Immagine a Prompt',
    'tools.imageToPromptDesc': 'Estrai prompt dettagliati da qualsiasi immagine',
    'tools.promptEnhancer': 'Miglioratore di Prompt',
    'tools.promptEnhancerDesc': 'Migliora e ottimizza i tuoi prompt',
    'tools.imageGenerator': 'Generatore di Immagini',
    'tools.imageGeneratorDesc': 'Crea immagini con IA',
    'tools.mockupGenerator': 'Generatore di Mockup',
    'tools.mockupGeneratorDesc': 'Crea mockup di prodotti',
    'tools.backgroundGenerator': 'Generatore di Sfondi',
    'tools.backgroundGeneratorDesc': 'Genera sfondi personalizzati',
    'tools.workflow': 'Flusso Completo',
    'tools.workflowDesc': 'Pipeline creativa completa',
    
    // FAQ Page
    'faq.title': 'Domande Frequenti',
    'faq.subtitle': 'Risposte rapide alle domande comuni',
    'faq.stillHaveQuestions': 'Hai ancora domande?',
    'faq.hereToHelp': 'Siamo qui per aiutarti',
    'faq.contactSupport': 'Contatta il Supporto',
    'faq.gettingStarted': 'Iniziare',
    'faq.billing': 'Fatturazione e Abbonamento',
    'faq.creditsUsage': 'Crediti e Utilizzo',
    'faq.features': 'Funzionalità',
    'faq.accountCancellation': 'Account e Cancellazione',
    
    // Contact Page
    'contact.title': 'Contattaci',
    'contact.subtitle': 'Domande, problemi o feedback? Siamo qui.',
    'contact.name': 'Nome',
    'contact.namePlaceholder': 'Il tuo nome',
    'contact.email': 'E-mail',
    'contact.emailPlaceholder': 'tu@esempio.com',
    'contact.topic': 'Argomento',
    'contact.topicPlaceholder': 'Seleziona un argomento',
    'contact.message': 'Messaggio',
    'contact.messagePlaceholder': 'Come possiamo aiutarti?',
    'contact.send': 'Invia Messaggio',
    'contact.sending': 'Invio...',
    'contact.directEmail': 'Oppure scrivici direttamente a',
    'contact.successTitle': 'Messaggio Inviato',
    'contact.successMessage': 'Ti risponderemo il prima possibile.',
    'contact.backToHome': 'Torna alla Home',
    'contact.sendAnother': 'Invia un Altro',
    'contact.topics.general': 'Domanda Generale',
    'contact.topics.technical': 'Problema Tecnico',
    'contact.topics.billing': 'Problema di Fatturazione',
    'contact.topics.feature': 'Richiesta Funzionalità',
    'contact.topics.feedback': 'Feedback',
    
    // Common
    'common.upload': 'Carica',
    'common.generate': 'Genera',
    'common.download': 'Scarica',
    'common.share': 'Condividi',
    'common.delete': 'Elimina',
    'common.cancel': 'Annulla',
    'common.save': 'Salva',
    'common.loading': 'Caricamento...',
    'common.credits': 'crediti',
    'common.error': 'Errore',
    'common.success': 'Successo',
  },
};

export const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'pt', name: 'Português', flag: '🇧🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
];

// Detect browser language and map to supported languages
const detectBrowserLanguage = () => {
  // Get browser language (e.g., 'en-US', 'es', 'fr-FR')
  const browserLang = navigator.language || navigator.userLanguage || 'en';
  
  // Extract the primary language code (e.g., 'en' from 'en-US')
  const primaryLang = browserLang.split('-')[0].toLowerCase();
  
  // Map to our supported languages
  const supportedLangs = ['en', 'es', 'fr', 'de', 'pt', 'it'];
  
  if (supportedLangs.includes(primaryLang)) {
    return primaryLang;
  }
  
  // Fallback to English
  return 'en';
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check if user has a stored preference
    const stored = localStorage.getItem('language');
    if (stored) {
      return stored;
    }
    
    // Auto-detect from browser locale on first visit
    const detected = detectBrowserLanguage();
    
    // Mark that we've done auto-detection
    localStorage.setItem('language_auto_detected', 'true');
    
    return detected;
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    const langDict = translations[language] || translations.en;
    return langDict[key] || translations.en[key] || key;
  };

  const changeLanguage = (newLang) => {
    if (languages.some(l => l.code === newLang)) {
      setLanguage(newLang);
    }
  };

  // Get the detected language (useful for showing a "detected" indicator)
  const detectedLanguage = detectBrowserLanguage();
  const wasAutoDetected = localStorage.getItem('language_auto_detected') === 'true';

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage: changeLanguage, 
      t, 
      languages,
      detectedLanguage,
      wasAutoDetected
    }}>
      {children}
    </LanguageContext.Provider>
  );
};
